/**
 * Что раздел «Финансы» спрашивает у сервера — клиент дашборда как есть.
 *
 * Экраны дашборда (`@finance/screens/*`) зовут `financeApi` из
 * `services/finance/web/src/api.ts` — адреса `/api/finance/*`, которые
 * Caddy портала отдаёт тому же `finance-api` (§4 ТЗ портала). Своего клиента
 * у раздела нет: второй набор адресов разошёлся бы с дашбордом.
 *
 * Единственное, что здесь добавлено, — показ без сервера. Клиент дашборда
 * ходит через `apiFetch` каркаса, а mock каркаса финансовых адресов не
 * знает. Поэтому при `VITE_MOCK_API=1` поля `financeApi` подменяются на
 * запросы через `request` портала — он отдаёт их `mock.ts` этой папки.
 * В боевую сборку ветка не попадает.
 */

import { financeApi } from '@finance/api';
import type { PeriodParams } from '@finance/api';
import { request } from '../../api';

export { financeApi };

const MOCK = import.meta.env.VITE_MOCK_API === '1';

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

let installed = false;

/** Подменить клиент дашборда на показ без сервера. Повторный вызов ничего не делает. */
export function installMockFinanceApi(force = false): void {
  if ((!MOCK && !force) || installed) return;
  installed = true;
  Object.assign(financeApi, {
    me: () => request('/api/finance/me'),
    overview: (params: PeriodParams = {}) => request(`/api/finance/overview${query({ ...params })}`),
    catalog: () => request('/api/finance/metrics'),
    metric: (code: string, params: PeriodParams = {}) =>
      request(`/api/finance/metrics/${encodeURIComponent(code)}${query({ ...params })}`),
    checks: (params: Record<string, string | number | undefined> = {}) =>
      request(`/api/finance/checks${query(params)}`),
    check: (id: number) => request(`/api/finance/checks/${id}`),
    data: () => request('/api/finance/data'),
    alerts: (params: Record<string, string | undefined> = {}) =>
      request(`/api/finance/alerts${query(params)}`),
    controlRules: () => request('/api/finance/control/rules'),
    saveRule: (body: unknown) => request('/api/finance/control/rules', { method: 'POST', body }),
    saveTarget: (body: unknown) =>
      request('/api/finance/control/targets', { method: 'POST', body }),
  });
}
